import { Input, Modal, Radio, message } from 'antd';
import { FC, useEffect, useRef, useState } from 'react';
import { UpdateButton } from '../../../../../lib/components/Buttons/UpdateButton';
import { InputItem } from '../../../../../lib/components/InputItem';
import {
	isValidCpf,
	maskCpf,
	sanitizeCpf,
} from '../../../../../lib/utils/cpfValidator';
import { SearchTagDto } from '../dto/SearchTagDto';
import { findSearchTagByCpf, useSearchTagsApi } from '../hooks/SearchTagsApi';

interface IProps {
	visible: boolean;
	close: () => void;
	update_success: () => void;
	tag: SearchTagDto | null;
	tag_name_in_portuguese: string;
}

const TAG_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ0-9\s.,\/ºª°-]+$/u;
const CPF_CHECK_DEBOUNCE_MS = 400;

export const SearchTagEditModal: FC<IProps> = ({
	visible,
	close,
	update_success,
	tag,
	tag_name_in_portuguese,
}) => {
	const isAuthor = tag?.prefix === 'author';
	const startedWithCpf = !!tag?.cpf;
	const { update } = useSearchTagsApi();

	const [tag_title, set_tag_title] = useState('');
	const [has_cpf, set_has_cpf] = useState(false);
	const [cpf, set_cpf] = useState('');
	const [cpf_check_loading, set_cpf_check_loading] = useState(false);
	const [cpf_conflict, set_cpf_conflict] = useState<{
		title: string;
		id: string;
	} | null>(null);
	const [loading, set_loading] = useState(false);
	const cpf_debounce_ref = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (visible && tag) {
			set_tag_title(tag.title || '');
			set_has_cpf(!!tag.cpf);
			set_cpf(tag.cpf ? maskCpf(tag.cpf) : '');
			set_cpf_conflict(null);
		}
		if (!visible) {
			set_tag_title('');
			set_has_cpf(false);
			set_cpf('');
			set_cpf_conflict(null);
			set_cpf_check_loading(false);
		}
	}, [visible, tag]);

	useEffect(() => {
		if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		set_cpf_conflict(null);

		if (!isAuthor || !has_cpf || !tag) return;
		const sanitized = sanitizeCpf(cpf);
		if (sanitized.length !== 11 || !isValidCpf(sanitized)) return;

		set_cpf_check_loading(true);
		cpf_debounce_ref.current = setTimeout(async () => {
			try {
				const existing = await findSearchTagByCpf(sanitized);
				if (existing && existing.id !== tag.id) {
					set_cpf_conflict({ title: existing.title, id: existing.id });
				}
			} catch (err) {
				console.error('Erro ao verificar CPF:', err);
			} finally {
				set_cpf_check_loading(false);
			}
		}, CPF_CHECK_DEBOUNCE_MS);

		return () => {
			if (cpf_debounce_ref.current) clearTimeout(cpf_debounce_ref.current);
		};
	}, [cpf, isAuthor, has_cpf, tag]);

	if (!tag) return null;

	const cpfRequired = isAuthor && (startedWithCpf || has_cpf);
	const sanitizedCpf = sanitizeCpf(cpf);
	const cpfFilled = sanitizedCpf.length > 0;
	const cpfFormatValid = sanitizedCpf.length === 11 && isValidCpf(sanitizedCpf);
	const cpfBlocking = cpfRequired && (!cpfFormatValid || !!cpf_conflict);

	const submit = async () => {
		const trimmed = tag_title.trim();
		if (!trimmed) {
			message.warning('Informe o título.');
			return;
		}
		if (!TAG_REGEX.test(trimmed)) {
			message.error(`Título inválido: ${trimmed}`);
			return;
		}
		if (cpfRequired) {
			if (!cpfFormatValid) {
				message.error('Informe um CPF válido.');
				return;
			}
			if (cpf_conflict) {
				message.error(
					`Já existe um autor cadastrado com este CPF: ${cpf_conflict.title}.`
				);
				return;
			}
		}

		set_loading(true);
		try {
			await update.do_update({
				id: tag.id,
				title: trimmed,
				prefix: tag.prefix,
				cpf: cpfRequired ? sanitizedCpf : undefined,
			});
			message.success(`${tag_name_in_portuguese} atualizada com sucesso!`);
			update_success();
			close();
		} catch (err: any) {
			const status = err?.response?.status;
			if (status === 409) {
				const existing = err?.response?.data?.existing;
				message.error(
					`Já existe um autor cadastrado com este CPF: ${existing?.title || ''}.`
				);
			} else {
				console.error('Erro ao atualizar tag:', err);
				message.error('Erro ao atualizar.');
			}
		} finally {
			set_loading(false);
		}
	};

	const submitDisabled = !tag_title || cpfBlocking || cpf_check_loading || loading;

	return (
		<Modal
			{...{
				visible,
				centered: true,
				closable: true,
				destroyOnClose: true,
				onCancel: () => close(),
				title: `Editar ${tag_name_in_portuguese}`,
				footer: [
					<UpdateButton
						{...{
							onClick: () => submit(),
							disabled: submitDisabled,
						}}
					/>,
				],
			}}
		>
			<InputItem
				{...{
					label: isAuthor ? 'Nome do autor' : 'Título',
					on_changed: set_tag_title,
					input_item_name: tag_title,
				}}
			/>

			{isAuthor && !startedWithCpf && (
				<div className='mt-4'>
					<label className='mr-4 font-semibold'>
						Você possui o CPF do autor que deseja cadastrar?
					</label>
					<div className='mt-2'>
						<Radio.Group
							value={has_cpf ? 'yes' : 'no'}
							onChange={(e) => set_has_cpf(e.target.value === 'yes')}
						>
							<Radio value='yes'>Sim</Radio>
							<Radio value='no'>Não</Radio>
						</Radio.Group>
					</div>
				</div>
			)}

			{cpfRequired && (
				<div className='mt-4'>
					<label className='mr-4 font-semibold'>
						<span className='text-red-500'> * </span>CPF:
					</label>
					<div className='w-5/6'>
						<Input
							{...{
								value: cpf,
								onChange: (event) => set_cpf(maskCpf(event.target.value)),
								placeholder: '000.000.000-00',
								maxLength: 14,
								allowClear: true,
							}}
						/>
					</div>
					<div className='mt-2 text-sm'>
						{cpf_check_loading && (
							<span style={{ color: '#888' }}>Verificando CPF...</span>
						)}
						{!cpf_check_loading && cpfFilled && !cpfFormatValid && (
							<span style={{ color: '#d4380d' }}>CPF inválido.</span>
						)}
						{!cpf_check_loading && cpf_conflict && (
							<span style={{ color: '#d4380d' }}>
								Já existe um autor cadastrado com este CPF:{' '}
								{cpf_conflict.title}.
							</span>
						)}
						{!cpf_check_loading &&
							cpfFilled &&
							cpfFormatValid &&
							!cpf_conflict && (
								<span style={{ color: '#52c41a' }}>CPF disponível.</span>
							)}
					</div>
				</div>
			)}
		</Modal>
	);
};
